import { inject, Injectable } from '@angular/core';
import { Exercise } from '../model/exercise';
import { Account } from '../model/account';
import { BehaviorSubject, Observable } from 'rxjs';
import { AccountParameters } from '../model/account-parameters';
import { Devise } from '../model/devise';
import { AccountParametersFactory } from '../model/factory/account-parameters-factory';
import {
  ConfigLoader,
  Factory,
  RestFactoryService,
  SettingsService,
} from '@infodata-group/inf-commons';
import { TranslateService } from '@ngx-translate/core';
import { AccountFactory } from '../model/factory/account-factory';
import { ExerciseFactory } from '../model/factory/exercise-factory';
import { Period } from '../model/period';

@Injectable({
  providedIn: 'root',
})
export class AccountManagerService {
  private rest = inject(RestFactoryService);
  private trl = inject(TranslateService);
  private loader = inject(ConfigLoader);
  private settings = inject(SettingsService);

  get exercise(): Exercise {
    return this._exercise;
  }

  set exercise(value: Exercise) {
    this._exercise = value;
    this.exerciseObs.next(value);
  }

  public account: Account;
  private _exercise: Exercise;
  public date: string;

  public saveRoute: string;

  private data = new BehaviorSubject<AccountParameters>(null);

  private exerciseObs = new BehaviorSubject<Exercise>(null);
  private parameters: AccountParameters;

  public isLoading = false;
  private isLoaded = new BehaviorSubject<boolean>(false);

  loadSettings(): Observable<boolean> {
    this.isLoaded = new BehaviorSubject<boolean>(false);
    this.initStorageData();
    this.isLoaded.next(true);
    return this.isLoaded;
  }

  isSettingsLoaded(): Observable<boolean> {
    return this.isLoaded.asObservable();
  }

  setAccountParameters(params: AccountParameters) {
    this.data.next(params);
    this.parameters = params;
  }

  getParams(): Observable<AccountParameters> {
    return this.data.asObservable();
  }

  getExercise(): Observable<Exercise> {
    return this.exerciseObs.asObservable();
  }

  findDevise(id: string): Devise {
    const devise = this.parameters.devises.find((item) => item.code === id);
    if (devise) {
      return devise;
    } else {
      return null;
    }
  }

  public initStorageData() {
    const af = new AccountFactory();
    const ef = new ExerciseFactory();
    this.account = this.factorize(af, this.settings.getSetting('account'));
    this.exercise = this.factorize(ef, this.settings.getSetting('exercise'));
    if (!this.isValid()) {
      this.clear();
    }
  }

  /**
   * Change context account
   * @return true if account is changed
   */
  changeAccount(account: Account): boolean {
    if (account) {
      if (!this.account || this.account.getId() !== account.getId()) {
        this.account = account;
        this.loadParams();
        return true;
      } else {
        return false;
      }
    }
  }

  public loadParams() {
    if (this.account) {
      const factory = new AccountParametersFactory(this.account);
      this.isLoading = true;
      this.rest.get(factory, { showLoading: false }).subscribe({
        next: (detail) => {
          const params = detail[0];
          this.settings.addSetting('account', this.account.serialize());
          this.initExercise(params);
          this.isLoading = false;
          this.setAccountParameters(params);
        },
        error: () => {
          this.clear();
          this.isLoading = false;
        },
      });
    }
  }

  changeExercise(exercise: Exercise) {
    if (exercise) {
      if (!this.exercise || this._exercise.code !== exercise.code) {
        this.exercise = exercise;
        this.settings.addSetting('exercise', this._exercise.serialize());
      }
    }
  }

  isValid(): boolean {
    return !!this.account && !!this._exercise;
  }

  private factorize<T>(factory: Factory<T>, serial: any): T {
    if (serial) {
      return factory.build(serial);
    }
    return null;
  }

  clear() {
    this.account = null;
    this.exercise = null;
  }

  /**
   * demande de SA sélectionner par défaut le dernier exercice et la derniere période
   * @param params
   * @private
   */
  private initExercise(params) {
    const exercise = params.exercises[0];
    this.changeExercise(exercise);
  }

  findExerciseByPeriod(period: Period): Exercise {
    if (period) {
      for (const exercise of this.parameters.exercises) {
        if (this.isPeriodOnExercise(exercise, period)) {
          return exercise;
        }
      }
    } else {
      return this.exercise;
    }
  }

  private isPeriodOnExercise(exercise: Exercise, period: Period) {
    return exercise.periods.find((per) => {
      return per.dateStartLabel === period.dateStartLabel;
    });
  }
}
